import { useEffect, useState } from "react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

import { Button } from "components/Button";
import { Modal } from "components/Modal";
import { PageWithNav } from "components/PageWithNav";
import { Spinner } from "components/Spinner";
import { Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "components/Table";
import api from "utils/api";

export const Courses = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteCourseId, setDeleteCourseId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadCourses = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/courses");
      setCourses(response.data.courses);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (courseId) => {
    setDeleteCourseId(courseId);
  };

  const confirm = async () => {
    try {
      setIsDeleting(true);
      await api.delete(`/course/${deleteCourseId}`);
      setDeleteCourseId(null);
      loadCourses();
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    loadCourses();
  }, []);

  return (
    <PageWithNav>
      <Modal isOpen={!!deleteCourseId} closeModal={() => setDeleteCourseId(null)} title="Are you sure?" description="This will permanently delete this course">
        <div className="mt-2 flex gap-2">
          <Button size="sm" onClick={confirm} loading={isDeleting} disabled={isDeleting}>
            Confirm
          </Button>
          <Button color="secondary" size="sm" onClick={() => setDeleteCourseId(null)}>
            Cancel
          </Button>
        </div>
      </Modal>
      <h1 className="mb-5 pe-4 text-xl font-semibold text-neutral-900">Courses</h1>

      <div className="my-4">
        <Button size="sm" onClick={() => navigate("/courses/create")}>
          Add New Course +
        </Button>
      </div>

      {isLoading && (
        <div className="flex justify-center">
          <Spinner size="lg" color="primary" />
        </div>
      )}

      {!isLoading && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell className="w-[0%]">ID</TableHeaderCell>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Created</TableHeaderCell>
              <TableHeaderCell />
            </TableRow>
          </TableHeader>
          <TableBody emptyColSpan={4}>
            {courses.length &&
              courses.map((course) => {
                return (
                  <TableRow key={course.id.toString()}>
                    <TableCell className="font-semibold">{course.id}</TableCell>
                    <TableCell>{course.name}</TableCell>
                    <TableCell>{format(course.created_at, "do MMM yyyy")}</TableCell>
                    <TableCell className="w-0">
                      <div className="flex gap-2">
                        <Button size="sm" color="secondary" onClick={() => navigate(`/courses/test/${course.id}`)}>
                          Test
                        </Button>
                        <Button size="sm" color="success" onClick={() => navigate(`/courses/lessons/${course.id}`)}>
                          Lessons
                        </Button>
                        <Button size="sm" onClick={() => navigate(`/courses/manage/${course.id}`)}>
                          Manage
                        </Button>
                        <Button size="sm" color="danger" onClick={() => handleDelete(course.id)}>
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      )}
    </PageWithNav>
  );
};
